import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, AlertTriangle } from "bizpandit-ui";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Feature-level boundaries can wrap individual
 * routes if a more granular fallback is needed; this is the last resort.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Wire this to your monitoring provider (Sentry, Datadog, etc.)
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <AlertTriangle className="h-10 w-10 text-caution" />
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="max-w-md text-sm text-navy2/60">
            {this.state.error?.message ?? "An unexpected error occurred. Please try again."}
          </p>
          <Button onClick={this.handleReset}>Reload app</Button>
        </div>
      );
    }

    return this.props.children;
  }
}
